import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

export interface UseUserDistributorGroupMembershipOptions {
  userId: string;
  organizationId: number;
  groupIds: number[];
  enabled?: boolean;
}

type DistributorGroupMembership = components['schemas']['CheckDistributorGroupMembershipEndpoint_Response'];

export function useUserDistributorGroupMembership({
  userId,
  organizationId,
  groupIds,
  enabled = true,
}: UseUserDistributorGroupMembershipOptions) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['user-distributor-group-membership', organizationId, userId, groupIds],
    queryFn: async ({ signal }) => {
      const client = createApiClient(getAccessToken, {
        cache: 'no-store',
        signal,
      });

      const { data, error } = await client.POST('/api/organizations/{organizationId}/users/{userId}/distributor-groups/check', {
        params: {
          path: { organizationId, userId },
        },
        body: { groupIds },
      });

      if (error) {
        throw new Error(error.title || 'Failed to check distributor group membership');
      }

      return (data ?? []) as DistributorGroupMembership[];
    },
    enabled: enabled && groupIds.length > 0,
    staleTime: 30000,
  });
}
